import type { ApplicationId, ObjectId, SpatialWindow, WindowFrame } from '@/types';
import type { ApplicationRegistry } from '@/applications/registry';
import type { SceneGraph } from '@/core/scene/sceneGraph';
import { visibleWorldBounds } from '@/core/coordinates/coordinateSystem';
import { useWorkspaceStore, selectActiveDocument } from '@/store/workspaceStore';
import { useCameraStore } from '@/store/cameraStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useWindowStore } from '@/store/windowStore';
import {
  addObjectsCommand,
  bringToFrontCommand,
  deleteObjectsCommand,
  nextZIndex,
} from '@/features/objects/objectCommands';
import { createWindow } from '@/features/objects/objectFactory';
import { centerOnObject } from '@/features/camera/cameraNavigation';
import { setWindowStateCommand } from './windowCommands';
import { placeWindow } from './windowPlacement';

/**
 * Window-manager operations. Durable effects (create, close, frame, state, z-order)
 * are document commands; focus and history live in the window store. Application UI
 * never calls these directly — the window chrome and shortcuts do.
 */

const doc = () => selectActiveDocument(useWorkspaceStore.getState());

export const listWindows = (): readonly SpatialWindow[] => {
  const d = doc();
  return d ? Object.values(d.objects).filter((o): o is SpatialWindow => o.kind === 'window') : [];
};

export const windowById = (id: ObjectId): SpatialWindow | undefined => {
  const o = doc()?.objects[id];
  return o?.kind === 'window' ? o : undefined;
};

/** Raises a window and marks it active. Z-order changes are view state: applied without history. */
export const focusWindow = (id: ObjectId): void => {
  const d = doc();
  const w = d?.objects[id];
  if (!d || w?.kind !== 'window') return;
  const maxZ = Math.max(0, ...Object.values(d.objects).map((o) => o.zIndex));
  if (w.zIndex < maxZ) useWorkspaceStore.getState().apply(bringToFrontCommand(d, [id]));
  useWindowStore.getState().focus(id);
};

export const openApplication = (applicationId: ApplicationId, applications: ApplicationRegistry): ObjectId | null => {
  const manifest = applications.get(applicationId);
  const d = doc();
  if (!manifest || !d) return null;
  if (!manifest.capabilities.multiInstance) {
    const existing = listWindows().find((w) => w.applicationId === applicationId);
    if (existing) {
      if (existing.windowState === 'minimized') restoreWindow(existing.id);
      focusWindow(existing.id);
      return existing.id;
    }
  }
  const { camera, viewport } = useCameraStore.getState();
  const visible = visibleWorldBounds(camera, viewport.size);
  const corners = listWindows()
    .filter((w) => w.windowState !== 'minimized')
    .sort((a, b) => a.metadata.createdAt - b.metadata.createdAt)
    .map((w) => w.transform.position);
  const position = placeWindow(manifest.defaultPosition, manifest.defaultSize, visible, corners);
  const win = createWindow({
    workspaceId: d.workspace.id,
    position,
    size: manifest.defaultSize,
    zIndex: nextZIndex(d),
    applicationId,
    name: manifest.name,
  });
  useWorkspaceStore.getState().dispatch(addObjectsCommand([win]));
  useWindowStore.getState().focus(win.id);
  useSelectionStore.getState().select([win.id]);
  return win.id;
};

export const closeWindow = (id: ObjectId): void => {
  const d = doc();
  if (!d || !windowById(id)) return;
  useWorkspaceStore.getState().dispatch(deleteObjectsCommand(d, [id]));
  useWindowStore.getState().forget(id);
  useSelectionStore.getState().deselect([id]);
};

const setState = (id: ObjectId, state: SpatialWindow['windowState'], frame?: WindowFrame) => {
  const d = doc();
  if (!d) return;
  const cmd = setWindowStateCommand(d, id, state, frame);
  if (cmd) useWorkspaceStore.getState().dispatch(cmd);
};

export const minimizeWindow = (id: ObjectId): void => {
  const w = windowById(id);
  if (!w) return;
  setState(id, 'minimized');
  useWindowStore.getState().forget(id);
};

export const restoreWindow = (id: ObjectId): void => {
  setState(id, 'normal');
  focusWindow(id);
};

/** Fills the current viewport (in world units) so the window is edge to edge at any zoom. */
export const maximizeWindow = (id: ObjectId): void => {
  const w = windowById(id);
  if (!w) return;
  const { camera, viewport } = useCameraStore.getState();
  const v = visibleWorldBounds(camera, viewport.size);
  const margin = 12 / camera.zoom;
  setState(id, 'maximized', {
    position: { x: v.minX + margin, y: v.minY + margin },
    size: { width: v.maxX - v.minX - margin * 2, height: v.maxY - v.minY - margin * 2 },
  });
  // A maximized window owns the viewport; the floating inspector would cover its controls.
  useSelectionStore.getState().clear();
  focusWindow(id);
};

export const toggleMaximize = (id: ObjectId): void => {
  const w = windowById(id);
  if (!w) return;
  if (w.windowState === 'maximized') restoreWindow(id);
  else maximizeWindow(id);
};

/** Focuses the next window in creation order (⌘` style cycling). */
export const cycleWindows = (direction: 1 | -1 = 1): void => {
  const wins = listWindows().filter((w) => w.windowState !== 'minimized');
  if (!wins.length) return;
  const active = useWindowStore.getState().activeWindowId;
  const idx = wins.findIndex((w) => w.id === active);
  const next = wins[(idx + direction + wins.length) % wins.length];
  if (next) focusWindow(next.id);
};

export const revealWindow = (scene: SceneGraph, id: ObjectId): void => {
  const w = windowById(id);
  if (!w) return;
  if (w.windowState === 'minimized') restoreWindow(id);
  else focusWindow(id);
  centerOnObject(scene, id);
};
